import React, { useEffect, useMemo, useState } from 'react';
import { Slider } from '@/components/ui/slider';
import { RefreshCcw, History } from 'lucide-react';
import GridLoader from '@/components/loading/GridLoader';
import clientNeedsIcon from '@/imgs/ClientNeeds/client-needs-icon.png';
import evaluationIcon from '@/imgs/ClientNeeds/evaluation-icon.png';
import HistoryInsightsDialog from '@/components/HistoryInsightsDialog';
// import { useLocation } from 'react-router-dom';
import { extractNumber } from '@/lib/utils';

interface ClientNeedsProps {
  onGeneratePlan?: (params: string) => void;
  generatePlanLoading?: boolean;
  totalData?: any;
  hasData?: boolean;
}

interface LevelText {
  expectedReturn: string;
  maxDrawdown: string;
  investmentAmount: string;
}

const ClientNeeds: React.FC<ClientNeedsProps> = ({
  hasData,
  onGeneratePlan,
  generatePlanLoading,
  totalData
}) => {
  // const location = useLocation();

  // 滑块值状态
  const [expectedReturn, setExpectedReturn] = useState([3]); // 3%-20%，默认25%位置
  const [maxDrawdown, setMaxDrawdown] = useState([1]); // 1%-30%，默认15%位置
  const [investmentAmount, setInvestmentAmount] = useState([1]); // 1万-1000万，默认50%位置
  // const [hasData, setHasData] = useState(location.state as { hasData: boolean });
  const [description, setDescription] = useState(
    '您的投资需求符合当前市场环境，建议采用均衡配置策略，注意分散投资风险'
  );
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  useEffect(() => {
    if (totalData) {
      setExpectedReturn([Number(extractNumber(totalData.returnRes || '3')) || 0]);
      setMaxDrawdown([Number(extractNumber(totalData.pullback || '1')) || 0]);
      setInvestmentAmount([Number(extractNumber(totalData.amount || '1')) || 0]);
    }
  }, [totalData]);

  const levelText = useMemo(() => {
    const obj: LevelText = {
      expectedReturn: '保守收益目标',
      maxDrawdown: '低风险承受能力',
      investmentAmount: '小规模投资'
    };
    if (expectedReturn[0] > 5) {
      obj.expectedReturn = '中等收益目标';
    }
    if (expectedReturn[0] > 10) {
      obj.expectedReturn = '积极收益目标';
      setDescription('您的投资需求符合当前市场环境，建议采用均衡配置策略，注意分散投资风险。');
    }
    if (expectedReturn[0] > 15) {
      obj.expectedReturn = '激进收益目标';
      setDescription(
        '您的投资需求较为激进，建议适当降低收益预期或提高风险承受能力，以实现更稳健的投资。'
      );
    }
    if (maxDrawdown[0] > 3) {
      obj.maxDrawdown = '中等风险承受能力';
    }
    if (maxDrawdown[0] > 8) {
      obj.maxDrawdown = '较高风险承受能力';
      setDescription('您的投资需求符合当前市场环境，建议采用均衡配置策略，注意分散投资风险。');
    }
    if (maxDrawdown[0] > 16) {
      obj.maxDrawdown = '高风险承受能力';
      setDescription(
        '您的投资需求较为激进，建议适当降低收益预期或提高风险承受能力，以实现更稳健的投资。'
      );
    }
    if (investmentAmount[0] > 50) {
      obj.investmentAmount = '中等规模投资';
    }
    if (investmentAmount[0] > 200) {
      obj.investmentAmount = '大规模投资';
      setDescription('您的投资需求符合当前市场环境，建议采用均衡配置策略，注意分散投资风险。');
    }
    if (investmentAmount[0] > 500) {
      obj.investmentAmount = '超大规模投资';
      setDescription(
        '您的投资需求较为激进，建议适当降低收益预期或提高风险承受能力，以实现更稳健的投资。'
      );
    }

    return obj;
  }, [expectedReturn, maxDrawdown, investmentAmount]);

  // 处理滑块值变化
  const handleExpectedReturnChange = (value: number[]) => {
    setExpectedReturn(value);
  };

  const handleMaxDrawdownChange = (value: number[]) => {
    setMaxDrawdown(value);
  };

  const handleGeneratePlan = () => {
    const params = {
      return_res: expectedReturn[0] + '%',
      pullback: maxDrawdown[0] + '%',
      amout: investmentAmount[0],
      drop_product: ''
    };
    onGeneratePlan?.(JSON.stringify(params));
  };

  const handleHistory = () => {
    setHistoryDialogOpen(true);
  };

  const handleInvestmentAmountChange = (value: number[]) => {
    setInvestmentAmount(value);
  };

  return (
    <div className="w-full space-y-3">
      {/* 标题栏 */}
      <div className="mb-4 mt-8 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <div className="flex h-[24px] w-[24px] items-center justify-center rounded">
            <img
              src={clientNeedsIcon}
              alt="客户投资需求"
              className="h-full w-full object-contain"
            />
          </div>
          <span className="text-base font-semibold text-black">客户投资需求</span>
        </div>
        {!generatePlanLoading &&
          (!hasData ? (
            <button
              onClick={handleGeneratePlan}
              className="rounded bg-gradient-to-r from-[#5D2BC7] to-[#A644CA] px-2 py-1 text-sm text-white shadow-sm transition-opacity hover:opacity-90"
            >
              方案生成
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleGeneratePlan}
                className="flex items-center rounded bg-gradient-to-r from-[#5D2BC7] to-[#A644CA] px-2 py-1 text-sm text-white shadow-sm transition-opacity hover:opacity-90"
              >
                <RefreshCcw className="mr-2" size={16} />
                重新生成
              </button>
              <button
                onClick={handleHistory}
                className="flex items-center rounded bg-gradient-to-r from-[#5D2BC7] to-[#A644CA] px-2 py-1 text-sm text-white shadow-sm transition-opacity hover:opacity-90"
              >
                <History className="mr-2" size={16} />
                历史方案
              </button>
            </div>
          ))}
      </div>

      {/* 配置卡片 */}
      <div className="space-y-3 rounded-lg border border-[#F0F0F0] bg-white p-3">
        {/* 期望年化收益 */}
        <div className="space-y-1">
          <div className="text-center text-sm font-semibold text-[#1F1F1F]">期望年化收益</div>
          <div className="flex items-center">
            <span className="w-12 whitespace-nowrap text-xs text-[#1F1F1F]">
              {expectedReturn[0]}%
            </span>
            <div className="relative flex-1">
              <Slider
                value={expectedReturn}
                onValueChange={handleExpectedReturnChange}
                max={20}
                min={3}
                step={1}
                className="w-full"
                thumbTooltip={
                  <div className="rounded-md bg-white px-2 py-1 shadow-lg">
                    <span className="whitespace-nowrap text-xs text-[#1F1F1F]">
                      {levelText.expectedReturn}
                    </span>
                  </div>
                }
              />
            </div>
            <span className="w-12 whitespace-nowrap text-right text-xs text-[#1F1F1F]">20%</span>
          </div>
        </div>

        {/* 最大回撤承受 */}
        <div className="space-y-1">
          <div className="text-center text-sm font-semibold text-[#1F1F1F]">最大回撤承受</div>
          <div className="flex items-center">
            <span className="w-12 whitespace-nowrap text-xs text-[#1F1F1F]">{maxDrawdown[0]}%</span>
            <div className="relative flex-1">
              <Slider
                value={maxDrawdown}
                onValueChange={handleMaxDrawdownChange}
                max={30}
                min={1}
                step={1}
                className="w-full"
                thumbTooltip={
                  <div className="rounded-md bg-white px-2 py-1 shadow-lg">
                    <span className="whitespace-nowrap text-xs text-[#1F1F1F]">
                      {levelText.maxDrawdown}
                    </span>
                  </div>
                }
              />
            </div>
            <span className="w-12 whitespace-nowrap text-right text-xs text-[#1F1F1F]">30%</span>
          </div>
        </div>

        {/* 可投资金额 */}
        <div className="space-y-1">
          <div className="text-center text-sm font-semibold text-[#1F1F1F]">可投资金额</div>
          <div className="flex items-center justify-between">
            <span className="w-12 whitespace-nowrap text-xs text-[#1F1F1F]">
              {investmentAmount[0]}
            </span>
            <div className="relative flex-1">
              <Slider
                value={investmentAmount}
                onValueChange={handleInvestmentAmountChange}
                max={1000}
                min={1}
                step={1}
                className="w-full"
                thumbTooltip={
                  <div className="rounded-md bg-white px-2 py-1 shadow-lg">
                    <span className="whitespace-nowrap text-xs text-[#1F1F1F]">
                      {levelText.investmentAmount}
                    </span>
                  </div>
                }
              />
            </div>
            <span className="w-12 text-right text-xs text-[#1F1F1F]">1000万</span>
          </div>
        </div>

        {/* 风险等级提示 */}
        {!generatePlanLoading && (
          <div className="border-gradient-to-r rounded-lg border bg-common-card-linear from-purple-200 to-pink-200 p-3">
            <div className="text-left text-xs leading-relaxed text-[#1F1F1F]">
              当前比例下，风险等级更新为：
              <span className="font-semibold text-[#5959EB]">
                {levelText.expectedReturn}、{levelText.maxDrawdown}、{levelText.investmentAmount}
              </span>
              。
            </div>
          </div>
        )}

        {/* 综合评估 */}
        {!generatePlanLoading && (
          <div className="rounded-lg border border-[#FF7B52] bg-[#FFF2E8] p-3">
            <div className="mb-2 flex items-center gap-1">
              <div className="flex h-[24px] w-[24px] items-center justify-center">
                <img src={evaluationIcon} alt="综合评估" className="h-full w-full object-contain" />
              </div>
              <span className="text-base font-semibold text-[#1F1F1F]">综合评估</span>
            </div>
            <div className="text-left text-xs leading-relaxed text-[#1F1F1F]">{description}</div>
          </div>
        )}
        {/* <div className="flex w-full flex-col items-center justify-center rounded-lg border border-dashed border-[#5959EB]/30 bg-[#F2F4FF]/30 py-6">
          {generatePlanLoading ? (
            <>
              <GridLoader color="#5959EB" count={4} cssOverride={{ width: '50px' }} />
              <span className="text-main w-1/2 text-sm">专属资产配置方案生成中…</span>
            </>
          ) : (
            <>
              <CircleAlert className="mb-2 text-[#5959EB]" size={20} fill="#5959EB" color="#fff" />
              <span className="text-main w-1/2 text-sm">
                请输入客户的期望年化收益、最大回撤承受、 可投资金额后查看评估总结…
              </span>
            </>
          )}
        </div> */}
        {generatePlanLoading && (
          <div className="flex w-full flex-col items-center justify-center rounded-lg border border-dashed border-[#5959EB]/30 bg-[#F2F4FF]/30 py-6">
            <GridLoader color="#5959EB" count={4} cssOverride={{ width: '50px' }} />
            <span className="w-1/2 text-sm text-main">专属资产配置方案生成中…</span>
          </div>
        )}
      </div>

      {/* 历史洞察 Dialog */}
      <HistoryInsightsDialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen} />
    </div>
  );
};

export default ClientNeeds;
